import React, { useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import CustomButton from '../CustomButton';
import {
    InvoiceDocument,
    useCorrectInvoiceMutation,
    useDispatchInvoiceMutation,
    useGetInvoiceDocumentsQuery,
    useGetIssuanceReadinessQuery,
    useIssueInvoiceMutation,
    useLazyGetDispatchRecipientsQuery,
    useLazyPreviewInvoiceQuery,
} from '../../api/invoice/invoiceApiSlice';
import { getCurrentLang } from '../../i18n/i18n';
import { useIsInvoiceIssuingTenant } from '../../hooks/useIsInvoiceIssuingTenant';
import { useInvoicePdfDownload } from '../../hooks/useInvoicePdfDownload';
import InvoiceDocumentTable from './InvoiceDocumentTable';
import InvoiceRecipientCheck from './InvoiceRecipientCheck';
import InvoiceNoteField from './InvoiceNoteField';
import InvoiceDueDateField from './InvoiceDueDateField';
import Toggle from '../ui/Toggle';
import { messageOf } from './invoiceMessages';
import { invoiceNotePrefill } from '../../utils/invoiceNotePrefill';

interface Props {
    orderId: string;
    /**
     * An organization covers this booking (ADR-0008, Layer 3). The person who
     * booked owes nothing, so no invoice is offered for them; the server
     * refuses it as well (`covered_by_org`). Documents issued before stay
     * listed and can still be cancelled.
     */
    isCovered?: boolean;
    /** The covering organization's name, when the detail response has it. */
    coveringOrganizationName?: string | null;
    /**
     * The booking is free BY PRICE (PO, 2026-10-23; `freeByPrice` on the detail
     * response): its list price is 0 €, so no invoice is needed and none is
     * offered — a note replaces preview and issue. The server refuses it as
     * well (`free_by_price`). A booking a discount brought to 0 € is not this
     * and is invoiced. Documents issued before stay listed.
     */
    isFreeByPrice?: boolean;
    /**
     * Where the booking's customer details are edited — the check before
     * issuing links there, because a wrong recipient is corrected on the
     * booking (ADR-0012 amendment 2026-10-12).
     */
    editBookingUrl?: string;
    /**
     * The tenant's default note (`InvoiceConfig.defaultInvoiceNote`), pre-filled
     * on an invoice paid in full — as the client-side dialog always did.
     */
    defaultNote?: string;
    /** Nothing is owed on the booking, so the default note applies. */
    isPaidInFull?: boolean;
    /**
     * The booking carries an admin's price adjustment (#3421, the saved
     * price's `reduction.kind`), so the operator chooses per invoice whether
     * it is stated as a row or folded into the line prices. A redeemed code
     * and a prepaid voucher are always stated, so they offer no choice.
     */
    hasPriceAdjustment?: boolean;
}

/** The per-invoice choice for a price adjustment (#3421); `offered` is false when the booking has none. */
type AdjustmentChoiceState = Readonly<{ offered: boolean, value: boolean, onChange: (value: boolean) => void }>;

type CorrectionMode = 'cancel' | 'replace';

/** A blank note is no note: it is left out of the request rather than sent empty. */
const noteOrUndefined = (note: string): string | undefined => note.trim() || undefined;

/** The picked due date, or undefined — the server then applies the tenant's Zahlungsziel. */
const dueDateOrUndefined = (day: string, show: boolean): string | undefined => (show && day) || undefined;

/** The due-date field, where an amount is open (#3427). */
const DueDate = ({
    id, show, value, disabled, onChange,
}: Readonly<{ id: string, show: boolean, value: string, disabled: boolean, onChange: (value: string) => void }>) => {
    const { t } = useTranslation();
    if (!show) return null;
    return (
        <InvoiceDueDateField
            id={id}
            label={t('account.invoice_due_date_label')}
            hint={t('account.invoice_due_date_hint')}
            value={value}
            disabled={disabled}
            onChange={onChange}
        />
    );
};

/** Stated or folded, where the booking has a price adjustment (#3421). */
const AdjustmentChoice = ({ choice, disabled }: Readonly<{ choice: AdjustmentChoiceState, disabled: boolean }>) => {
    const { t } = useTranslation();
    if (!choice.offered) return null;
    return (
        <Toggle
            checked={choice.value}
            onChange={choice.onChange}
            label={t('account.invoice_show_adjustment_label')}
            description={t('account.invoice_show_adjustment_hint')}
            disabled={disabled}
        />
    );
};

/**
 * Which "cannot issue yet" message to show, given what readiness says is
 * missing.
 *
 * The circle comes first deliberately: it is the only precondition the TENANT
 * can act on, and the only one that is normal rather than a misconfiguration —
 * every Mode A tenant passes through it exactly once. An environment gap is
 * kolula's to fix and much rarer, so it is the fallback.
 */
const notReadyKey = (missing: string[]): string => {
    if (missing.includes('number_circle')) return 'account.invoice_not_ready_circle';
    if (missing.includes('circle_unusable')) return 'account.invoice_not_ready_circle_unusable';
    if (missing.includes('seller_incomplete')) return 'account.invoice_not_ready_seller';
    if (missing.includes('einvoice_seller_identifier')) return 'account.invoice_not_ready_einvoice_seller';
    // This booking's invoice would state an open amount, so it needs the IBAN
    // it prints (PO, 2026-10-19) — the tenant deposits it in Account. A booking
    // paid in full is never reported here.
    if (missing.includes('bank_details')) return 'account.invoice_not_ready_bank_details';
    if (missing.includes('einvoice_unavailable')) return 'account.invoice_not_ready_einvoice';
    return 'account.invoice_not_ready';
};


/**
 * Why no invoice can be issued for this booking right now, or null.
 *
 * Coverage first, then free by price: both are facts about the booking that no
 * tenant setting changes, so a missing number circle is not the reason worth
 * telling. Then
 * WHICH precondition is missing — seeding a number circle and an unconfigured
 * environment have different next actions and different owners (#2255).
 */
const issueBlockedReason = (
    isCovered: boolean,
    isFreeByPrice: boolean,
    coveringOrganizationName: string | null,
    readiness: { ready: boolean, missing: string[] } | undefined,
    t: TFunction,
): string | null => {
    if (isCovered) {
        return coveringOrganizationName
            ? t('account.invoice_covered_named', { organization: coveringOrganizationName })
            : t('account.invoice_covered');
    }
    if (isFreeByPrice) return t('account.invoice_free_by_price');
    if (readiness && !readiness.ready) return t(notReadyKey(readiness.missing));
    return null;
};

/**
 * Preview and issue, or the reason neither is offered.
 *
 * Preview first, deliberately: issuing is irreversible — it allocates the
 * gap-free number and archives the document under a COMPLIANCE lock — so the
 * cheap, reversible action reads first. The issue button does not issue: it
 * opens the recipient check (`InvoiceRecipientCheck`), which does.
 *
 * Not clickable until readiness has answered: before it does, a click could
 * only earn a refusal the page was about to show anyway (#2840 A1). A FAILED
 * readiness read leaves it usable — the server states its own refusal on click.
 */
const IssueControls = ({
    blockedReason, isPreviewing, isIssuing, isWaiting, note, onNoteChange, dueDate, showDueDate, onDueDateChange,
    adjustment, onPreview, onIssue,
}: Readonly<{
    blockedReason: string | null;
    isPreviewing: boolean;
    isIssuing: boolean;
    isWaiting: boolean;
    /** Above the buttons, so the preview already shows it. */
    note: string;
    onNoteChange: (value: string) => void;
    dueDate: string;
    showDueDate: boolean;
    onDueDateChange: (value: string) => void;
    adjustment: AdjustmentChoiceState;
    onPreview: () => void;
    onIssue: () => void;
}>) => {
    const { t } = useTranslation();
    if (blockedReason) return <p className={'text-foreground/60'}>{blockedReason}</p>;
    return (
        <>
            <InvoiceNoteField
                id={'invoice-issue-note'}
                label={t('account.invoice_note_label')}
                hint={t('account.invoice_note_hint')}
                value={note}
                disabled={isIssuing}
                onChange={onNoteChange}
            />
            <DueDate
                id={'invoice-issue-due-date'}
                show={showDueDate}
                value={dueDate}
                disabled={isIssuing}
                onChange={onDueDateChange}
            />
            <AdjustmentChoice choice={adjustment} disabled={isIssuing} />
            <div className={'flex flex-wrap gap-3'}>
                <CustomButton
                    title={t('account.invoice_preview')}
                    isFilled={false}
                    loading={isPreviewing}
                    disabled={isPreviewing || isWaiting}
                    onClick={onPreview}
                />
                <CustomButton
                    title={t('account.invoice_issue')}
                    loading={isIssuing || isWaiting}
                    disabled={isIssuing || isWaiting}
                    onClick={onIssue}
                />
            </div>
        </>
    );
};

/**
 * Send a live invoice, behind an inline form that SHOWS the address first.
 *
 * The address is never sent on a bare click: the operator sees where it is
 * going and can change it, because an invoice reaching the wrong inbox is a
 * failure nobody discovers — the customer simply never mentions a document
 * they never got. The pre-fill comes from the server's precedence (the
 * customer organisation's invoice address, else the tenant's own dispatch
 * address, else the person who booked) and `source` labels which it was.
 *
 * Inline rather than the shared `ConfirmationDialog`, for the same reason the
 * correction controls are: that one is driven by global state the booking page
 * already uses.
 */
const SendControls = ({
    live,
    isSending,
    onSend,
}: Readonly<{
    live: InvoiceDocument;
    isSending: boolean;
    onSend: (to: string, source: string) => void;
}>) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [to, setTo] = useState('');
    const [source, setSource] = useState('manual');
    const [loadRecipients, { isFetching }] = useLazyGetDispatchRecipientsQuery();

    const begin = async () => {
        setOpen(true);
        try {
            const suggestion = await loadRecipients(live.id).unwrap();
            setTo(suggestion.to[0] ?? '');
            setSource(suggestion.source);
        } catch {
            // No suggestion is not an error: the operator types the address.
            // Falling back to an empty field is honest; pre-filling a guess
            // would not be.
            setTo('');
            setSource('manual');
        }
    };

    if (!open) {
        return <CustomButton title={t('account.invoice_send')} isFilled={false} onClick={begin} />;
    }

    return (
        <div className={'border border-foreground/10 rounded-[5px] p-4 mt-2'}>
            <p className={'text-sm text-foreground mb-2'}>
                {t('account.invoice_send_confirm', { number: live.number })}
            </p>
            <label className={'block text-sm text-foreground/60 mb-1'} htmlFor={'invoice-send-to'}>
                {t('account.invoice_send_to')}
            </label>
            <input
                id={'invoice-send-to'}
                type={'email'}
                value={to}
                disabled={isFetching || isSending}
                onChange={(e) => { setTo(e.target.value); setSource('manual'); }}
                className={'w-full border border-foreground/20 rounded-[5px] px-3 py-2 text-sm mb-1'}
            />
            {/* Which precedence level produced the pre-fill, so the operator
                knows whether this is the customer or an internal inbox. */}
            <p className={'text-xs text-foreground/60 mb-3'}>
                {t(`account.invoice_send_source_${source}`)}
            </p>
            <div className={'flex flex-wrap gap-3'}>
                <CustomButton
                    title={t('account.invoice_send_confirm_button')}
                    loading={isSending}
                    disabled={isSending || isFetching || to.trim() === ''}
                    onClick={() => onSend(to.trim(), source)}
                />
                <CustomButton
                    title={t('account.invoice_correction_back')}
                    isFilled={false}
                    disabled={isSending}
                    onClick={() => setOpen(false)}
                />
            </div>
        </div>
    );
};

/**
 * Cancel / correct a live invoice, behind an inline confirmation. Inline rather
 * than the shared `ConfirmationDialog`: that one is driven by global state the
 * booking page already uses for its own dialogs.
 */
const CorrectionControls = ({
    orderId,
    editBookingUrl,
    live,
    pendingMode,
    isCorrecting,
    note,
    onNoteChange,
    dueDate,
    showDueDate,
    onDueDateChange,
    adjustment,
    onChoose,
    onConfirm,
}: Readonly<{
    orderId: string;
    editBookingUrl?: string;
    live: InvoiceDocument;
    pendingMode: CorrectionMode | null;
    isCorrecting: boolean;
    /** The note for the document the correction is about — see `correctInvoice`. */
    note: string;
    onNoteChange: (value: string) => void;
    /** The replacement invoice's due date. */
    dueDate: string;
    showDueDate: boolean;
    onDueDateChange: (value: string) => void;
    /** The replacement's price adjustment, stated or folded. */
    adjustment: AdjustmentChoiceState;
    onChoose: (mode: CorrectionMode | null) => void;
    onConfirm: () => void;
}>) => {
    const { t } = useTranslation();

    if (!pendingMode) {
        return (
            <div className={'flex flex-wrap gap-3'}>
                <CustomButton title={t('account.invoice_replace')} isFilled={false} onClick={() => onChoose('replace')} />
                <CustomButton title={t('account.invoice_cancel')} isFilled={false} onClick={() => onChoose('cancel')} />
            </div>
        );
    }

    // A replacement issues a new invoice, so it passes the same recipient
    // check as a first issue: the reason for replacing is often the address.
    if (pendingMode === 'replace') {
        return (
            <InvoiceRecipientCheck
                orderId={orderId}
                intro={t('account.invoice_replace_confirm', { number: live.number })}
                confirmTitle={t('account.invoice_replace_confirm_button')}
                editBookingUrl={editBookingUrl}
                busy={isCorrecting}
                onConfirm={onConfirm}
                onBack={() => onChoose(null)}
            >
                <InvoiceNoteField
                    id={'invoice-replace-note'}
                    label={t('account.invoice_note_label')}
                    hint={t('account.invoice_note_hint')}
                    value={note}
                    disabled={isCorrecting}
                    onChange={onNoteChange}
                />
                <DueDate
                    id={'invoice-replace-due-date'}
                    show={showDueDate}
                    value={dueDate}
                    disabled={isCorrecting}
                    onChange={onDueDateChange}
                />
                <AdjustmentChoice choice={adjustment} disabled={isCorrecting} />
            </InvoiceRecipientCheck>
        );
    }

    const isCancel = pendingMode === 'cancel';
    return (
        <div className={'border border-foreground/10 rounded-[5px] p-4 mt-2'}>
            <p className={'text-sm text-foreground mb-3'}>
                {t(isCancel ? 'account.invoice_cancel_confirm' : 'account.invoice_replace_confirm', { number: live.number })}
            </p>
            <InvoiceNoteField
                id={'invoice-cancel-note'}
                label={t('account.invoice_cancellation_note_label')}
                hint={t('account.invoice_cancellation_note_hint')}
                value={note}
                disabled={isCorrecting}
                onChange={onNoteChange}
            />
            <div className={'flex flex-wrap gap-3'}>
                <CustomButton
                    title={t(isCancel ? 'account.invoice_cancel_confirm_button' : 'account.invoice_replace_confirm_button')}
                    loading={isCorrecting}
                    disabled={isCorrecting}
                    onClick={onConfirm}
                />
                <CustomButton
                    title={t('account.invoice_correction_back')}
                    isFilled={false}
                    disabled={isCorrecting}
                    onClick={() => onChoose(null)}
                />
            </div>
        </div>
    );
};

/**
 * Server-side invoices on a booking (#1637 WP2) and their corrections (#1643).
 *
 * Shows every document ever issued for the booking — the original, its
 * cancellation, a replacement — oldest first, with what the chain nets to.
 * That is the audit view the ticket asks for: an issued invoice is never
 * edited, so the history IS the record.
 *
 * Renders nothing at all for a Mode C tenant: their own system owns the number
 * and the document, and offering a button that allocates a competing statutory
 * number would be worse than offering none.
 */
const IssueInvoiceSection = ({
    orderId, isCovered = false, isFreeByPrice = false, coveringOrganizationName = null, editBookingUrl, defaultNote,
    isPaidInFull = false, hasPriceAdjustment = false,
}: Readonly<Props>) => {
    const { t } = useTranslation();
    // The note for the next invoice: null until the operator edits it, so the
    // pre-fill follows the booking's payment state until then instead of being
    // frozen at whatever it was on first render.
    const [noteDraft, setNoteDraft] = useState<string | null>(null);
    const [correctionNote, setCorrectionNote] = useState('');
    // Empty = the tenant's Zahlungsziel, applied by the server.
    const [dueDate, setDueDate] = useState('');
    const showDueDate = !isPaidInFull;
    // #3421: stated by default, as every invoice was before the choice existed.
    const [showAdjustment, setShowAdjustment] = useState(true);
    const adjustment: AdjustmentChoiceState = { offered: hasPriceAdjustment, value: showAdjustment, onChange: setShowAdjustment };
    // Sent only where there is a choice; otherwise the server's default stands.
    const showPriceAdjustment = hasPriceAdjustment ? showAdjustment : undefined;
    // Issuing goes through the recipient check first (ADR-0012 amendment
    // 2026-10-12): the admin confirms who the invoice is addressed to.
    const [checking, setChecking] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const [pendingMode, setPendingMode] = useState<CorrectionMode | null>(null);
    const [sent, setSent] = useState<string | null>(null);

    // The DISPLAYED tenant's mode, via the shared hook — a master has no
    // tenant of their own, and reading the caller's account made this section
    // invisible on production, where kolula staff are masters.
    const isIssuingTenant = useIsInvoiceIssuingTenant();

    // `skip` rather than a conditional hook: a Mode C tenant must not even ask
    // the server about issuance.
    // Per booking: `bank_details` depends on whether THIS invoice states an
    // open amount. Refetched on mount so a payment recorded meanwhile counts.
    const { data: readiness, isLoading: readinessLoading } = useGetIssuanceReadinessQuery(
        orderId,
        { skip: !isIssuingTenant, refetchOnMountOrArgChange: true },
    );
    const { data: chain, isLoading, isError } = useGetInvoiceDocumentsQuery(orderId, { skip: !isIssuingTenant });
    const [issue, { isLoading: isIssuing }] = useIssueInvoiceMutation();
    const [correct, { isLoading: isCorrecting }] = useCorrectInvoiceMutation();
    const downloadPdf = useInvoicePdfDownload();
    const [preview, { isLoading: isPreviewing }] = useLazyPreviewInvoiceQuery();
    const [dispatch, { isLoading: isSending }] = useDispatchInvoiceMutation();

    if (!isIssuingTenant) return null;

    const lang = getCurrentLang() as 'de' | 'en';
    const issueNote = noteDraft ?? invoiceNotePrefill(defaultNote, isPaidInFull, lang);
    const documents = chain?.documents ?? [];
    const live = documents.find((d) => d.documentType === 'invoice' && d.status === 'issued') ?? null;

    const onIssue = async () => {
        setFailure(null);
        try {
            await issue({
                orderId, language: lang, note: noteOrUndefined(issueNote), dueDate: dueDateOrUndefined(dueDate, showDueDate),
                showPriceAdjustment,
            }).unwrap();
            setChecking(false);
            setNoteDraft(null);
        } catch (err: unknown) {
            setFailure(messageOf(err, t) ?? t('account.invoice_issue_failed'));
        }
    };

    const onConfirmCorrection = async () => {
        if (!live || !pendingMode) return;
        setFailure(null);
        try {
            await correct({
                invoiceId: live.id,
                orderId,
                mode: pendingMode,
                language: lang,
                note: noteOrUndefined(correctionNote),
                // Only a replacement is an invoice with an amount to pay.
                dueDate: pendingMode === 'replace' ? dueDateOrUndefined(dueDate, showDueDate) : undefined,
                showPriceAdjustment: pendingMode === 'replace' ? showPriceAdjustment : undefined,
            }).unwrap();
            setPendingMode(null);
            setCorrectionNote('');
        } catch (err: unknown) {
            setFailure(messageOf(err, t) ?? t('account.invoice_correction_failed'));
        }
    };

    /**
     * Open the preview in a new tab.
     *
     * Opened rather than downloaded: the point is to READ the document before
     * committing to it, and a file in the downloads folder is a worse place to
     * read it from. The blob URL is revoked on a timer rather than straight
     * away, because the new tab has not finished loading from it yet when this
     * function returns — revoking immediately gives a blank tab, which would
     * look exactly like a broken preview.
     */
    const onPreview = async () => {
        setFailure(null);
        try {
            const blob = await preview({
                orderId, language: lang, note: noteOrUndefined(issueNote), dueDate: dueDateOrUndefined(dueDate, showDueDate),
                showPriceAdjustment,
            }).unwrap();
            const url = URL.createObjectURL(blob);
            // No `noopener` in the features, deliberately: with it set,
            // `window.open` returns null BY SPECIFICATION even when the tab
            // opened, so the blocked-popup check below would report a failure
            // on every success. It costs nothing here — a `blob:` URL is our
            // own origin, so there is no third-party document to sever.
            const opened = window.open(url, '_blank');
            if (!opened) {
                // A blocked popup must not look like a working preview that
                // simply showed nothing: the button would stop loading and the
                // operator would be left guessing.
                URL.revokeObjectURL(url);
                setFailure(t('account.invoice_preview_blocked'));
                return;
            }
            // Revoked on a timer rather than straight away: the new tab has
            // not finished loading from the URL when this returns, and
            // revoking immediately gives a blank tab — indistinguishable from
            // a broken preview.
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err: unknown) {
            setFailure(messageOf(err, t) ?? t('account.invoice_preview_failed'));
        }
    };

    /**
     * Send the live invoice.
     *
     * NOT fail-open, unlike every other customer mail here: sending IS what
     * the operator asked for, so a failure has to reach them. A success state
     * on a failed send is how #2218's six-day outage stayed invisible.
     */
    const onSend = async (to: string, source: string) => {
        setFailure(null);
        setSent(null);
        if (!live) return;
        try {
            const result = await dispatch({ invoiceId: live.id, to: [to], source }).unwrap();
            setSent(result.to.join(', '));
        } catch (err: unknown) {
            setFailure(messageOf(err, t) ?? t('account.invoice_send_failed'));
        }
    };

    const onDownload = async (doc: InvoiceDocument) => {
        setFailure(null);
        try {
            await downloadPdf(doc);
        } catch (err: unknown) {
            setFailure(messageOf(err, t) ?? t('account.invoice_download_failed'));
        }
    };

    return (
        <div className={'bg-text-contrast rounded-[5px] shadow-sm p-5'}>
            <h2 className={'text-base font-semibold text-foreground mb-1'}>
                {t('account.invoice_issuer_section')}
            </h2>

            {/* A failure must never look like an absence: a chain that could
                not be loaded is not a booking without invoices. */}
            {isError ? (
                <p className={'text-sm text-foreground/80'}>{t('account.invoice_documents_failed')}</p>
            ) : null}

            {documents.length > 0 ? (
                <InvoiceDocumentTable
                    documents={documents}
                    netGross={chain?.netGross ?? '0'}
                    lang={lang}
                    onDownload={onDownload}
                />
            ) : null}

            {live ? (
                <div className={'flex flex-col gap-3'}>
                    {/* Send first: it is what an operator does next with a
                        just-issued invoice, while correcting is the exception. */}
                    {!pendingMode ? (
                        <SendControls live={live} isSending={isSending} onSend={onSend} />
                    ) : null}
                    <CorrectionControls
                        orderId={orderId}
                        editBookingUrl={editBookingUrl}
                        live={live}
                        pendingMode={pendingMode}
                        isCorrecting={isCorrecting}
                        note={correctionNote}
                        onNoteChange={setCorrectionNote}
                        dueDate={dueDate}
                        showDueDate={showDueDate}
                        onDueDateChange={setDueDate}
                        adjustment={adjustment}
                        onChoose={(mode) => {
                            setPendingMode(mode);
                            // Cancel starts empty (a refund account is never
                            // guessed); a replacement is a new invoice and
                            // starts like one.
                            setCorrectionNote(mode === 'replace' ? invoiceNotePrefill(defaultNote, isPaidInFull, lang) : '');
                        }}
                        onConfirm={onConfirmCorrection}
                    />
                </div>
            ) : null}

            {/* No live invoice: never issued, or the last one was cancelled —
                either way the booking can be invoiced (again). */}
            {!live && !isError ? (
                <div className={'text-sm text-foreground mt-2'}>
                    {checking ? (
                        <InvoiceRecipientCheck
                            orderId={orderId}
                            confirmTitle={t('account.invoice_recipient_confirm')}
                            editBookingUrl={editBookingUrl}
                            busy={isIssuing}
                            onConfirm={onIssue}
                            onBack={() => setChecking(false)}
                        >
                            <InvoiceNoteField
                                id={'invoice-issue-note'}
                                label={t('account.invoice_note_label')}
                                hint={t('account.invoice_note_hint')}
                                value={issueNote}
                                disabled={isIssuing}
                                onChange={setNoteDraft}
                            />
                            <DueDate
                                id={'invoice-issue-due-date'}
                                show={showDueDate}
                                value={dueDate}
                                disabled={isIssuing}
                                onChange={setDueDate}
                            />
                            <AdjustmentChoice choice={adjustment} disabled={isIssuing} />
                        </InvoiceRecipientCheck>
                    ) : (
                        <IssueControls
                            blockedReason={issueBlockedReason(isCovered, isFreeByPrice, coveringOrganizationName, readiness, t)}
                            isPreviewing={isPreviewing}
                            isIssuing={isIssuing}
                            isWaiting={isLoading || readinessLoading}
                            note={issueNote}
                            onNoteChange={setNoteDraft}
                            dueDate={dueDate}
                            showDueDate={showDueDate}
                            onDueDateChange={setDueDate}
                            adjustment={adjustment}
                            onPreview={onPreview}
                            onIssue={() => { setFailure(null); setChecking(true); }}
                        />
                    )}
                </div>
            ) : null}

            {sent ? (
                <p className={'mt-3 text-sm text-foreground/80'}>
                    {t('account.invoice_send_done', { to: sent })}
                </p>
            ) : null}
            {failure ? <p className={'mt-3 text-sm text-foreground/80'}>{failure}</p> : null}
        </div>
    );
};

export default IssueInvoiceSection;
